import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { GeneratePdfContentType } from 'src/pages/MyLearningPage/types';
import { sendUIEvent } from 'src/utils/analytics';
import { addToCalendar, CalendarEvent } from 'src/utils/calendar';

import { generateLiveTrainingCSV } from '../utils/generateLiveTrainingCSV';
import { generatePdf } from '../utils/generatePdf';
import { type LiveTrainingSession } from './useLiveTrainingFilters';

interface UseLiveTrainingActionsParams {
  liveTrainingData: LiveTrainingSession[];
}

export type { CalendarEvent };

interface UseLiveTrainingActionsReturn {
  isLearningHistoryLoading: boolean;
  isCompletionCertificateLoading: boolean;
  handleLearningHistoryClick: () => Promise<void>;
  handleBrowseCalendar: () => void;
  handleDetailsClick: (courseId: string, instanceId: string) => void;
  handleAddToCalendar: (calendarEvent: CalendarEvent) => void;
  handleDownloadMaterials: (courseMaterialsUrl: string) => void;
  handleDownloadCompletionCertificate: (title: string, endDateTime: string) => Promise<void>;
  handleToggleSaved: (courseId: string, instanceId: string, isSaved: boolean) => void;
}

export const useLiveTrainingActions = ({
  liveTrainingData,
}: UseLiveTrainingActionsParams): UseLiveTrainingActionsReturn => {
  const navigate = useNavigate();
  const { user } = useAtlassianUser();
  const [isLearningHistoryLoading, setIsLearningHistoryLoading] = useState(false);
  const [isCompletionCertificateLoading, setIsCompletionCertificateLoading] = useState(false);

  const handleLearningHistoryClick = async (): Promise<void> => {
    if (!liveTrainingData.length || !user) {
      return;
    }

    setIsLearningHistoryLoading(true);

    try {
      // Generate and download CSV
      generateLiveTrainingCSV({
        userName: user.name || 'user',
        liveTrainingData,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error generating learning history CSV:', error);
    } finally {
      // Reset loading state after a brief delay to show feedback
      setTimeout(() => {
        setIsLearningHistoryLoading(false);
      }, 500);
    }
  };

  const handleBrowseCalendar = (): void => {
    navigate('/calendar');
  };

  const handleDetailsClick = (courseId: string, instanceId: string): void => {
    navigate(`/class/${courseId}/session/${instanceId}`);
  };

  const handleAddToCalendar = (calendarEvent: CalendarEvent): void => {
    addToCalendar(calendarEvent);
  };

  const handleDownloadMaterials = (courseMaterialsUrl: string): void => {
    window.open(courseMaterialsUrl, '_blank');
  };

  const handleToggleSaved = (courseId: string, instanceId: string, isSaved: boolean): void => {
    if (!user) {
      return;
    }

    const savedId = `${courseId}_${instanceId}`;

    // eslint-disable-next-line no-console
    console.log('Toggle saved:', savedId, isSaved);
    // TODO: uncomment when BE updates to support live training saved status are complete
    // saveOrUpdateContentSavedStatus(savedId, ContentType.LIVE_TRAINING, isSaved);
  };

  const handleDownloadCompletionCertificate = async (title: string, endDateTime: string): Promise<void> => {
    if (!user) {
      return;
    }

    setIsCompletionCertificateLoading(true);

    try {
      // Send analytics event
      sendUIEvent(
        'myLearningScreen',
        'button',
        'clicked',
        'downloadPDF',
        {
          resourceSlug: title,
          resourceType: GeneratePdfContentType.LIVE_TRAINING,
        },
        user.account_id,
      );

      // Generate PDF with endDateTime as completion date
      await generatePdf({
        title,
        completionDate: endDateTime,
        userName: user.name || 'User',
        imageUrl: undefined, // Will use atlassianLogoUrl for LIVE_TRAINING
        contentType: GeneratePdfContentType.LIVE_TRAINING,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error generating PDF:', error);
    } finally {
      // Reset loading state after a brief delay to show feedback
      setTimeout(() => {
        setIsCompletionCertificateLoading(false);
      }, 500);
    }
  };

  return {
    isLearningHistoryLoading,
    isCompletionCertificateLoading,
    handleLearningHistoryClick,
    handleBrowseCalendar,
    handleDetailsClick,
    handleAddToCalendar,
    handleDownloadMaterials,
    handleDownloadCompletionCertificate,
    handleToggleSaved,
  };
};
